import React from 'react';
import { Sidebar } from 'primereact/sidebar';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';

// Imóveis de interesse dos leads da área, agrupados por nome. O mapa Google
// (GeoReferenciamentoMapa/Table.jsx) listava só os nomes distintos; a contagem sai
// de graça do mesmo agrupamento.
const imoveisDeInteresse = (leads) => {
    const contagem = leads.reduce((acc, lead) => {
        const nome = String(lead.properties?.property_of_interest || '').trim();
        if (!nome) return acc;

        acc[nome] = (acc[nome] || 0) + 1;

        return acc;
    }, {});

    return Object.entries(contagem)
        .map(([nome, total]) => ({ nome, total }))
        .sort((a, b) => b.total - a.total || a.nome.localeCompare(b.nome));
};

const formatarDistancia = (metros) => metros < 1000
    ? `${metros.toLocaleString('pt-BR')} m`
    : `${(metros / 1000).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} km`;

// Ícone por tipo de usuário (lead/visitante/cliente). Não existe marcador próprio
// por tipo — no mapa o pino distingue só o gênero —, então o tipo ganha aqui um
// ícone do FontAwesome, já carregado pelo layout.
const TIPO_USUARIO = {
    lead: { icon: 'fa-user-plus', color: '#e0a800', label: 'Lead' },
    visitante: { icon: 'fa-user-clock', color: '#17a2b8', label: 'Visitante' },
    cliente: { icon: 'fa-user-check', color: '#28a745', label: 'Cliente' },
};
const TIPO_PADRAO = { icon: 'fa-user', color: '#6c757d', label: 'Usuário' };

// Lista dos usuários ordenada pela distância até o empreendimento selecionado.
// Clicar numa linha centraliza o mapa no usuário.
function LeadsPorDistancia({ leads, onCenterLead }) {
    if (leads.length === 0) {
        return (
            <p className="text-muted small mb-0">
                Nenhum usuário no mapa para calcular distâncias.
            </p>
        );
    }

    // Mesmo pino que o usuário tem no mapa (masc/fem), para a linha da lista ser
    // reconhecível ali; o ícone do tipo vem logo ao lado do rótulo.
    const marcadorTemplate = (lead) => (
        <img
            className="geo-lead-marker"
            src={lead.icon === 'lead_fem' ? '/img/markers/fem.png' : '/img/markers/masc.png'}
            alt={lead.genre === 'f' ? 'Usuário feminino' : 'Usuário masculino'}
            title={lead.genre === 'f' ? 'Usuário feminino' : 'Usuário masculino'}
        />
    );

    const usuarioTemplate = (lead) => {
        const tipo = TIPO_USUARIO[String(lead.user_type || '').toLowerCase()] || TIPO_PADRAO;
        const endereco = [lead.street, lead.number, lead.neighborhood]
            .filter((x) => x && String(x).length)
            .join(', ');

        return (
            <div className="geo-lead-endereco">
                <span className="geo-lead-tipo" style={{ color: tipo.color }}>
                    <i className={`fa-solid ${tipo.icon}`} /> {tipo.label}
                </span>
                <small className="d-block text-muted">{endereco || lead.city || '—'}</small>
            </div>
        );
    };

    return (
        <DataTable
            value={leads}
            className="p-datatable-sm geo-leads-table"
            paginator={leads.length > 10}
            rows={10}
            selectionMode="single"
            onRowClick={(e) => onCenterLead(e.data)}
        >
            <Column body={marcadorTemplate} style={{ width: '2.5rem' }} />
            <Column body={usuarioTemplate} header="Usuário" />
            <Column
                field="distancia"
                header="Distância"
                body={(lead) => formatarDistancia(lead.distancia)}
                style={{ width: '6.5rem', textAlign: 'right' }}
            />
        </DataTable>
    );
}

function AreaCard({ area, onCenterArea }) {
    const imoveis = imoveisDeInteresse(area.leads);

    return (
        <div className="geo-area-card" style={{ borderLeft: `4px solid ${area.color}` }}>
            <div className="geo-area-card-header" style={{ backgroundColor: area.color }}>
                <strong>{area.name}</strong>
                <Button
                    icon="pi pi-map-marker"
                    className="p-button-text p-button-sm geo-area-card-center"
                    title="Centralizar mapa na área"
                    onClick={() => onCenterArea(area)}
                />
            </div>

            <div className="geo-area-card-body">
                <div className="geo-area-meta">
                    Tipo da busca: {area.type}
                    {area.radiusMeters > 0 && (
                        <> · Raio: {Math.round(area.radiusMeters).toLocaleString('pt-BR')} m</>
                    )}
                </div>

                <div className="geo-area-stats">
                    <div>
                        <span className="geo-area-stat-value">{area.leads.length.toLocaleString('pt-BR')}</span>
                        <span className="geo-area-stat-label">Usuários localizados</span>
                    </div>
                    <div>
                        <span className="geo-area-stat-value">{area.buildings.length.toLocaleString('pt-BR')}</span>
                        <span className="geo-area-stat-label">Imóveis localizados</span>
                    </div>
                </div>

                <label className="geo-area-table-title">Imóveis de interesse</label>
                <DataTable
                    value={imoveis}
                    emptyMessage="Nenhum imóvel de interesse informado pelos leads da área."
                    className="p-datatable-sm"
                    paginator={imoveis.length > 10}
                    rows={10}
                >
                    <Column field="nome" header="Nome" />
                    <Column field="total" header="Leads" style={{ width: '5rem', textAlign: 'right' }} />
                </DataTable>
            </div>
        </div>
    );
}

/**
 * Painel de resultados por área desenhada — sucessor de
 * GeoReferenciamentoMapa/{SlidingPanelEmpreendimento,InfoCard,Table}.jsx, que usavam
 * react-sliding-side-panel e jQuery para abrir/fechar.
 */
export default function GeoResultsPanel({
    visible,
    escondido,
    areas,
    selectedBuilding,
    leadsPorDistancia,
    loadingBuildingDetail,
    onShow,
    onHide,
    onClearSelectedBuilding,
    onCenterArea,
    onCenterLead,
    onOpenBuilding,
}) {
    // O painel também abre sem áreas desenhadas: o cálculo de distância até um
    // empreendimento vale para todos os usuários carregados, e é aqui que fica a
    // indicação de qual empreendimento está selecionado (e como limpar).
    if (areas.length === 0 && !selectedBuilding) return null;

    return (
        <>
            {/* Aleta lateral: a mesma abre e fecha o painel, acompanhando a borda dele
                (substitui o "x" da Sidebar e o antigo botão de reabrir). */}
            {!escondido && (
                <button
                    type="button"
                    className={`mapbox-flap mapbox-flap-right${visible ? ' is-open' : ''}`}
                    title={visible ? 'Ocultar resultados' : 'Mostrar resultados'}
                    aria-expanded={visible}
                    onClick={visible ? onHide : onShow}
                >
                    <i className={`fas ${visible ? 'fa-angle-double-right' : 'fa-chart-bar'}`} />
                    {!visible && areas.length > 0 && (
                        <span className="mapbox-flap-badge">{areas.length}</span>
                    )}
                </button>
            )}

            {/* A largura vem do CSS (--mapbox-right-panel-width) para a aleta poder
                acompanhar a borda do painel. */}
            <Sidebar
                visible={visible}
                position="right"
                onHide={onHide}
                modal={false}
                dismissable={false}
                showCloseIcon={false}
                className="mapbox-right-panel"
            >
                <div className="mapbox-right-panel-header">
                    <strong>Resultados</strong>
                    {areas.length > 0 && (
                        <span className="badge badge-secondary ml-2">
                            {areas.length} {areas.length === 1 ? 'área' : 'áreas'}
                        </span>
                    )}
                </div>

                <div className="mapbox-right-panel-body">
                    {selectedBuilding && (
                        <div className="geo-selected-building">
                            <div className="geo-selected-building-header">
                                <div>
                                    <small className="text-muted d-block">Distâncias até</small>
                                    {/* Abre a ficha completa (mesmo modal do mapa Mapbox principal). */}
                                    <button
                                        type="button"
                                        className="geo-selected-building-name"
                                        title="Ver dados do empreendimento"
                                        onClick={() => onOpenBuilding(selectedBuilding.id)}
                                        disabled={loadingBuildingDetail}
                                    >
                                        {selectedBuilding.name}
                                        <i className={loadingBuildingDetail
                                            ? 'fa fa-spin fa-spinner ml-1'
                                            : 'fas fa-external-link-alt ml-1'} />
                                    </button>
                                    <small className="text-muted d-block">
                                        {leadsPorDistancia.length.toLocaleString('pt-BR')} usuários no mapa,
                                        do mais próximo ao mais distante. Clique numa linha para
                                        centralizar o mapa.
                                    </small>
                                </div>
                                <Button
                                    icon="pi pi-times"
                                    className="p-button-text p-button-sm"
                                    title="Limpar seleção"
                                    onClick={onClearSelectedBuilding}
                                />
                            </div>

                            <LeadsPorDistancia leads={leadsPorDistancia} onCenterLead={onCenterLead} />
                        </div>
                    )}

                    {areas.length === 0 ? (
                        <p className="text-muted small mb-0">
                            Desenhe uma área no mapa para ver os totais de usuários e
                            imóveis e os imóveis de interesse dentro dela.
                        </p>
                    ) : (
                        areas.map((area) => (
                            <AreaCard key={area.drawId} area={area} onCenterArea={onCenterArea} />
                        ))
                    )}
                </div>
            </Sidebar>
        </>
    );
}
